import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { self } from "../lib/api";
import { isOperatorAuthenticated, subscribeAuth } from "../lib/auth";

export function OperatorAuthGate() {
  const [authed, setAuthed] = useState(() => isOperatorAuthenticated());
  // Email verification state — null = unknown, true = ok, false = block.
  // /auth/me is the source of truth; the gate fetches it once per token
  // change. The server also rejects unverified tokens at the
  // CurrentOperator dep, so this is a UX nicety (redirect to inbox-
  // check screen) rather than the security boundary.
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [meEmail, setMeEmail] = useState<string>("");
  // Accounts that must visit onboarding before the app: SSO shells
  // (role=unaffiliated) AND onboarded property managers with zero properties.
  // null = unknown until /auth/me resolves.
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => subscribeAuth(() => setAuthed(isOperatorAuthenticated())), []);

  useEffect(() => {
    if (!authed) {
      setEmailVerified(null);
      setNeedsOnboarding(null);
      return;
    }
    let cancelled = false;
    self
      .me()
      .then((r) => {
        if (cancelled) return;
        setEmailVerified(r.email_verified);
        setMeEmail(r.email);
        setNeedsOnboarding(Boolean(r.needs_onboarding) || Boolean(r.needs_property_setup));
        // Register for native push + init in-app purchases once we have a
        // verified, onboarded session (both no-op off native).
        if (r.email_verified && !r.needs_onboarding) {
          void import("../lib/push").then((m) => m.initPush()).catch(() => {});
          void import("../lib/iap").then((m) => m.iapInit(r.id)).catch(() => {});
        }
      })
      .catch(() => {
        if (!cancelled) setEmailVerified(null);
      });
    return () => {
      cancelled = true;
    };
    // Re-fetch when navigating to/from onboarding so completing it releases the gate.
  }, [authed, location.pathname]);

  if (!authed) {
    return <Navigate to={`/signin?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (emailVerified === false) {
    return (
      <Navigate
        to={`/verify-pending?email=${encodeURIComponent(meEmail)}`}
        replace
      />
    );
  }
  const onOnboarding = location.pathname === "/app/onboarding";
  if (needsOnboarding === true && !onOnboarding) {
    return <Navigate to="/app/onboarding" replace />;
  }
  // An onboarded operator that lands on /app/onboarding gets sent home.
  if (needsOnboarding === false && onOnboarding) {
    return <Navigate to="/app/today" replace />;
  }
  return <Outlet />;
}
